/**
 * Seeded pseudo-random numbers. The only randomness the sim is allowed to use.
 *
 * mulberry32: small, fast, good enough for game rolls, and identical in every JS runtime.
 * A game gets one seed (issued by the server); every roll in that game derives from it in order,
 * which is what makes replays byte-identical.
 */
export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform float in [min, max]. */
  roll(min: number, max: number): number;
  /** True with probability p. */
  chance(p: number): boolean;
  /** Uniform integer in [0, n). */
  int(n: number): number;
  /** Number of values drawn so far; useful in tests and replays. */
  readonly draws: number;
}

export function createRng(seed: number): Rng {
  let a = seed >>> 0;
  let draws = 0;
  const next = (): number => {
    draws += 1;
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    roll: (min, max) => min + (max - min) * next(),
    chance: (p) => next() < p,
    int: (n) => Math.floor(next() * n),
    get draws() {
      return draws;
    },
  };
}

/** Derive a stable 32-bit seed from a string (e.g. a game id). FNV-1a. */
export function seedFromString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
